import { type SearchModuleTranslationKey } from '@pkgs/components/types/typedTranslationKeys';
import type { ComponentTFunction } from '@pkgs/components/providers/AppContextProvider';
import { CountryCode, ImageDisturber, PropertyMarketingType } from '@ev-tech/search-modules-api';

export type BadgeItem = {
  label: string;
  variant: ImageDisturber;
};

export type GetBadgesParams = {
  imageDisturbers?: ImageDisturber[];
  translationResolver: ComponentTFunction;
  countryAlpha2?: CountryCode;
  propertyMarketingType?: PropertyMarketingType;
};

export const getCountrySpecificReservedBadgeTranslationKey = ({
  countryAlpha2,
  propertyMarketingType,
}: {
  countryAlpha2?: CountryCode;
  propertyMarketingType?: PropertyMarketingType;
}): SearchModuleTranslationKey => {
  if (countryAlpha2 !== CountryCode.IE) return 'search.disturber.label.reserved';
  if (propertyMarketingType === PropertyMarketingType.sale) return 'search.disturber.label.saleAgreed';
  if (propertyMarketingType === PropertyMarketingType.rent) return 'search.disturber.label.letAgreed';
  return 'search.disturber.label.reserved';
};

const getBadgeTranslationKey = ({
  variant,
  countryAlpha2,
  propertyMarketingType,
}: {
  variant: ImageDisturber;
  countryAlpha2?: CountryCode;
  propertyMarketingType?: PropertyMarketingType;
}): SearchModuleTranslationKey | '' => {
  switch (variant) {
    case ImageDisturber.EXCLUSIVE:
      return 'search.disturber.label.exclusive';
    case ImageDisturber.NEW:
      return 'search.disturber.label.new';
    case ImageDisturber.PRICE_UPDATED:
      return 'search.disturber.label.price_updated';
    case ImageDisturber.RENTED_OUT:
      return 'search.disturber.label.rented_out';
    case ImageDisturber.RESERVED:
      return getCountrySpecificReservedBadgeTranslationKey({ countryAlpha2, propertyMarketingType });
    case ImageDisturber.SOLD:
      return 'search.disturber.label.sold';
    case ImageDisturber.UNLOCKED:
      return 'search.disturber.label.unlocked';
    default:
      return '';
  }
};

export const getBadges = ({
  imageDisturbers = [],
  translationResolver: t,
  countryAlpha2,
  propertyMarketingType,
}: GetBadgesParams): BadgeItem[] => {
  return imageDisturbers.map((disturber) => {
    const translationKey = getBadgeTranslationKey({ variant: disturber, countryAlpha2, propertyMarketingType });

    return {
      label: translationKey ? t(translationKey) : '',
      variant: disturber,
    };
  });
};
